import lunr, { Token } from "lunr";
import {
  charPrefixOrSuffixExceptions,
  charWithinStrExceptions,
  SEPARATOR,
  specialCharacterReplacements,
  stopWords,
  wordToNumber,
} from "../../utils/searchPrototypeData";
import { HyphenatedToken, MiscSearchData } from "../../types";

export function lunrSpecialCasePlugIn(builder: lunr.Builder) {
  const pipelineFunction = function (currToken: Token) {
    const currTokenStr = currToken.toString();
    // the stemmer will convert "one"/"ones" to "on", which is a stop word, so we need to handle it as a special case
    if (currTokenStr === "one" || currTokenStr === "ones") {
      const updatedToken = currToken.update(() => "1");
      return updatedToken;
    }
    return currToken;
  };
  lunr.Pipeline.registerFunction(pipelineFunction, "handleSpecialCases");
  builder.pipeline.before(lunr.stemmer, pipelineFunction);
  builder.searchPipeline.before(lunr.stemmer, pipelineFunction);
}

export function lunrNumberPlugIn(builder: lunr.Builder) {
  const pipelineFunction = function (currToken: Token) {
    const currTokenStr = currToken.toString();
    const isNumber = Object.hasOwn(wordToNumber, currTokenStr);
    if (isNumber) {
      const updatedToken = currToken.update(() => wordToNumber[currTokenStr]);
      return updatedToken;
    }
    return currToken;
  };
  lunr.Pipeline.registerFunction(pipelineFunction, "handleNumbers");
  builder.pipeline.after(lunr.stemmer, pipelineFunction);
  builder.searchPipeline.after(lunr.stemmer, pipelineFunction);
}

function customTrimmer(token: Token) {
  return token.update(function (s) {
    const hasPrefixOrSuffixException = Object.hasOwn(
      charPrefixOrSuffixExceptions,
      s
    );
    if (hasPrefixOrSuffixException) {
      return s;
    }

    const hasSpecialCharacterReplacement = Object.hasOwn(
      specialCharacterReplacements,
      s
    );
    if (hasSpecialCharacterReplacement) {
      return specialCharacterReplacements[s];
    }

    const trimmedStr = s
      .replace(/^\W+/, "")
      .replace(/\W+$/, "")
      .replace(/('s)$/, "");

    const hasWithinStrException = Object.hasOwn(
      charWithinStrExceptions,
      trimmedStr
    );
    if (hasWithinStrException) {
      return charWithinStrExceptions[trimmedStr];
    }

    return trimmedStr;
  });
}

export function lunrCustomTrimmerPlugIn(builder: lunr.Builder) {
  lunr.trimmer = customTrimmer;

  lunr.Pipeline.registerFunction(lunr.trimmer, "customTrimmer"); // replaces the default trimmer for the index pipeline
}

export function hyphenPlugin(builder: lunr.Builder) {
  function hyphenator(token: lunr.Token) {
    // if there are no hyphens then skip this logic
    if (!token.toString().includes("-")) return token;

    // split the token by hyphens, returning a clone of the original token with the split
    // e.g. 'anti-virus' -> 'anti', 'virus'
    const tokens = token
      .toString()
      .split("-")
      .map(function (s) {
        return token.clone(() => s);
      });

    // clone the token and replace any hyphens
    // e.g. 'anti-virus' -> 'antivirus'
    tokens.push(token.clone((s: string) => s.replaceAll("-", "")));

    // finally push the original token into the list
    // 'anti-virus' -> 'anti-virus'
    tokens.push(token.clone((s: string) => s));

    // send the tokens on to the next step of the pipeline
    return tokens;
  }
  lunr.Pipeline.registerFunction(hyphenator, "hyphenator");
  builder.pipeline.before(lunr.trimmer, hyphenator);
}

export function whitelistPlugin(builder: lunr.Builder, whitelist: string[]) {
  function stopWordWhitelist(token: lunr.Token) {
    // pass any token in the whitelist through
    if (whitelist.includes(token.toString())) return token;
    // all other tokens should be passed through the normal filter
    return lunr.stopWordFilter(token);
  }

  lunr.Pipeline.registerFunction(stopWordWhitelist, "stopWordWhitelist");

  builder.pipeline.before(lunr.stopWordFilter, stopWordWhitelist);
  builder.pipeline.remove(lunr.stopWordFilter);
}

export function lunrSearch(
  query: string,
  index: lunr.Index,
  miscSearchData: MiscSearchData
): lunr.Index.Result[] {
  const { wildcardWords, hyphenatedWords } = miscSearchData;

  const processedQuery = hyphenatedWords
    ? checkQueryForCommonHyphenatedToken(query, hyphenatedWords)
    : query;
  const tokens = lunr.tokenizer(processedQuery);
  const processedTokens = processTokens(tokens);

  if (processedTokens.every((tokenSet) => tokenSet.length === 0)) {
    return [];
  }

  const results: lunr.Index.Result[] = [];
  processedTokens.forEach((tokenSet: lunr.Token[]) => {
    const queryResults = index.query(function (q) {
      tokenSet.forEach((currToken: lunr.Token) => {
        let currTokenStr = currToken.toString();

        const hasSpecialCharacterReplacement = Object.hasOwn(
          specialCharacterReplacements,
          currTokenStr
        );
        if (hasSpecialCharacterReplacement) {
          currTokenStr = specialCharacterReplacements[currTokenStr];
        }

        const wildcard = wildcardWords?.find((key) =>
          currTokenStr.startsWith(key)
        );
        const isWildcard = !!wildcard;
        if (wildcard) {
          currTokenStr = wildcard;
        }

        const isStopWord = stopWords.includes(currTokenStr);

        const settings = {
          presence: isStopWord
            ? lunr.Query.presence.OPTIONAL
            : lunr.Query.presence.REQUIRED,
          usePipeline: true,
          ...(isWildcard
            ? {
                wildcard: lunr.Query.wildcard.TRAILING,
                usePipeline: false,
              }
            : {}),
        };
        q.term(currTokenStr, settings);
      });
    });
    results.push(...queryResults);
  });

  const uniqueResults = results.filter((result, index) => {
    return results.findIndex((r) => r.ref === result.ref) === index;
  });
  return uniqueResults;
}

function checkQueryForCommonHyphenatedToken(
  query: string,
  commonHyphenatedTokens: HyphenatedToken
) {
  const lowerCaseQuery = query.toLowerCase();
  Object.keys(commonHyphenatedTokens).forEach((key) => {
    const hyphenatedToken = commonHyphenatedTokens[key];
    if (lowerCaseQuery.includes(key)) {
      query = lowerCaseQuery.replaceAll(key, hyphenatedToken);
    }
  });
  return query;
}

function processTokens(tokens: lunr.Token[]): lunr.Token[][] {
  const hyphenatedTokenSets = generateTokenSetsFromHyphenatedQuery(tokens);
  const processedTokenSets = hyphenatedTokenSets.map((tokenSet) =>
    removeEmptyTokens(tokenSet.map((token) => customTrimmer(token)))
  );

  return processedTokenSets;
}

function removeEmptyTokens(tokens: lunr.Token[]): lunr.Token[] {
  return tokens.filter((token) => token.toString() !== "");
}

function generateTokenSetsFromHyphenatedQuery(
  tokens: lunr.Token[]
): lunr.Token[][] {
  if (!tokens.some((token) => token.toString().includes("-"))) {
    return [tokens];
  }

  // Case 1: Split hyphenated tokens
  const tokenSet1 = mapHyphenatedTokens(tokens, (token) =>
    token
      .toString()
      .split("-")
      .map((s) => token.clone(() => s))
  );

  // Case 2: Remove hyphens
  const tokenSet2 = mapHyphenatedTokens(tokens, (token) => [
    token.clone((s: string) => s.replaceAll("-", "")),
  ]);

  // Case 3: Keep as is
  const tokenSet3 = mapHyphenatedTokens(tokens, (token) => [
    token.clone((s: string) => s),
  ]);

  return [tokenSet1, tokenSet2, tokenSet3];
}

function mapHyphenatedTokens(
  tokens: lunr.Token[],
  hyphenTransform: (token: lunr.Token) => lunr.Token[]
): lunr.Token[] {
  const result: lunr.Token[] = [];
  tokens.forEach((token) => {
    if (token.toString().includes("-")) {
      result.push(...hyphenTransform(token));
    } else {
      result.push(token);
    }
  });
  return result;
}

lunr.tokenizer.separator = SEPARATOR;
